/**
 * JWT request header + refresh on 401. Refresh itself lives in authSession so
 * the default axios instance and httpClient share one in-flight request.
 */
import { getActivePinia } from 'pinia';
import { useUserStore } from '../stores/userStore';
import { reportApiError } from './errorReports';
import {
  ACCESS_KEY,
  isCredentialRequest,
  isRefreshRequest,
  refreshSessionTokens,
  requestUrl,
} from './authSession';

function getApiBase() {
  return (process.env.VUE_APP_API_BASE_URL || '').replace(/\/$/, '');
}

function isApiRequest(config) {
  const url = requestUrl(config);
  if (url.indexOf('/api/') !== -1) return true;
  const base = getApiBase();
  return !!(base && url.startsWith(base));
}

function bearerToken(config) {
  const headers = config && config.headers;
  if (!headers) return null;
  const value = headers.Authorization || headers.authorization || '';
  const match = /^Bearer\s+(.+)$/i.exec(String(value));
  return match ? match[1] : null;
}

function activeStore() {
  try {
    const pinia = getActivePinia();
    return pinia ? useUserStore(pinia) : null;
  } catch {
    return null;
  }
}

function publishTokens(result) {
  const store = activeStore();
  if (store && result.status === 'ok') {
    store.setTokens(result.access, result.refresh);
  }
}

function expireStoredSession() {
  const store = activeStore();
  if (store) {
    store.expireSession();
    return;
  }
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
}

/**
 * @param {import('axios').AxiosInstance} axiosInstance
 */
export function applyAuthInterceptors(axiosInstance) {
  axiosInstance.interceptors.request.use((config) => {
    if (!isApiRequest(config) || isRefreshRequest(config) || isCredentialRequest(config)) return config;
    const access = localStorage.getItem(ACCESS_KEY);
    if (access) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${access}`;
    }
    return config;
  });

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (
        !originalRequest
        || error.response?.status !== 401
        || !isApiRequest(originalRequest)
        || isRefreshRequest(originalRequest)
        || isCredentialRequest(originalRequest)
      ) {
        return Promise.reject(error);
      }
      if (originalRequest._retried) {
        expireStoredSession();
        return Promise.reject(error);
      }

      let result;
      try {
        result = await refreshSessionTokens(bearerToken(originalRequest));
      } catch {
        return Promise.reject(error);
      }
      if (result.status === 'ok' && result.access) {
        publishTokens(result);
        originalRequest._retried = true;
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${result.access}`;
        return axiosInstance(originalRequest);
      }
      if (result.status === 'auth') {
        expireStoredSession();
      }
      return Promise.reject(error);
    },
  );

  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      reportApiError(error);
      return Promise.reject(error);
    },
  );
}
